<template>
  <v-container fluid class="pa-0 pb-8">
    <v-container class="container-custom">
      <Breadcrumbs :title="$t('modules.admission.preregistration.create.breadcrumbs.title')"
        :description="$t('modules.admission.preregistration.create.breadcrumbs.description')"></Breadcrumbs>
      <v-row justify="center">
        <v-col class="viewSpaces" sm="12">
          <div :class="showEmailSummary ? 'd-none' : 'd-flex'">
            <div class="col-8 pl-0 pt-0">
              <v-stepper class="mb-3">
                <v-stepper-header class="stepper-header-height">
                  <v-stepper-step step="1" class="py-4" :complete="!!hubspotId" editable edit-icon="$complete"
                    :color="hubspotId ? 'success' : 'primary'" @click="scrollToSection('leadInfo')">
                    {{ $t('modules.admission.preregistration.create.stepper.first') }}
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="2" class="py-4" :complete="!!selectedBenefit && !!selectedScholarship" editable
                    edit-icon="$complete" :color="selectedBenefit && selectedScholarship ? 'success' : 'primary'"
                    @click="scrollToSection('priceSimulator')">
                    {{ $t('modules.admission.preregistration.create.stepper.second') }}
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="3" class="py-4" :complete="validStudentData" editable edit-icon="$complete"
                    :color="validStudentData ? 'success' : 'primary'" @click="scrollToSection('preregistrationInfo')">
                    {{ $t('modules.admission.preregistration.create.stepper.fourth') }}
                  </v-stepper-step>
                </v-stepper-header>
              </v-stepper>
              <div ref="preregistrationScroll" class="preregistration-scroll">
                <LeadInfo ref="leadInfo" :careers="careers" :canCreate="canCreate"
                  :installmentsSimulationButtonDisabled="(total.prices.length === 0 || !selectedInstallments)"
                  @nextStep="enablePriceSimulator" @emailChange="(changedEmail) => { this.email = changedEmail }"
                  @nameChange="(changedName) => { this.name = changedName }"
                  @studyPlanChange="(changedStudyPlan) => { this.studyPlan = changedStudyPlan }"
                  @telephoneChange="(changedTelephone) => { this.telephone = changedTelephone }"
                  @tokenChanged="(changedToken) => { this.benefitToken = changedToken }"
                  @installmentsChange="(changedInstallments) => { this.selectedInstallments = changedInstallments }"
                  @openInstallmentsModal="openInstallmentsModal"></LeadInfo>
                <PriceSimulator ref="priceSimulator" :show="!!prices.length" :prices="prices" :discounts="discounts"
                  :benefits="benefits" :student-token="studentToken" :scholarships="scholarships" :total="total"
                  :selectedModality="this.selectedModality" @averageChange="averageChange" @selectedPrices="(changedPrice) => {
                    this.selectedPrices = changedPrice
                    if (changedPrice[0] && changedPrice[0].term && changedPrice[0].modality) {
                      this.preregStudentData.selectedSaleTerm = changedPrice[0].term
                      this.selectedModality = changedPrice[0].modality.id
                    }
                  }" @setBenefit="(changedBenefit) => { this.selectedBenefit = changedBenefit }"
                  @setReferredFriend="(changedReferredFriend) => { this.referredFriend = changedReferredFriend }"
                  @setScholarship="(changedScholarship) => { this.selectedScholarship = changedScholarship }"
                  @selectedDiscounts="(selected) => { this.selectedDiscounts = selected }"
                  @applyDiscount="(changedDiscount) => { applyDiscount(changedDiscount) }">
                </PriceSimulator>
                <PreregistrationInfo ref="preregistrationInfo" :email="email" :name="name" :careers="careers"
                  :career="selectedCareer" :studyPlan="studyPlan" :city="HARDCODED_CITY" :telephone="telephone"
                  :saleTerms="saleTerms" :checkbox="true"
                  @setStudentData="(changedStudentData) => { this.preregStudentData = changedStudentData }"
                  @careerChange="(changedCareer) => { enablePriceSimulator(changedCareer, this.selectedZone, this.hubspotId) }"
                  @withoutPaymentRequired="(withoutPaymentValue) => { this.withoutPayment = withoutPaymentValue }"
                  @withoutDocumentationRequired="(withoutDocumentationValue) => { this.withoutDocumentation = withoutDocumentationValue }"
                  @validationError="createPreregistrationError" @validationSuccess="createPreregistration">
                </PreregistrationInfo>
                <div class="d-flex justify-end pa-3">
                  <Button white :text="$t('actions.cancel')" @clicked="$router.replace('/admission/preregistration')"
                    @end="resetButtonValues" class="mr-4"></Button>
                  <Button :loading="createButton.loading" :success="createButton.success" :error="createButton.error"
                    :disabled="createButton.disabled"
                    :text="isCreated ? $t('actions.create_another') : $t('actions.create')"
                    :successText="$t('actions.created')" :errorText="$t('actions.error')" @end="resetButtonValues"
                    @clicked="isCreated ? clearData() : validatePreregistration()"></Button>
                </div>
              </div>
            </div>
            <Summary class="col-4" :prepaids="prepaids" :dues="dues" :total="total" :is-created="isCreated"
              :selected-installments="selectedInstallments" :loading-installments="loadingInstallments"
              :has-installment-details="installmentDetails.length > 0" :payment-link-button="paymentLinkButton"
              @showEmailSummary="() => { this.showEmailSummary = true }" @sendPaymentLink="sendPaymentLink"></Summary>
          </div>
          <EmailSummary v-if="showEmailSummary" :prepaids="prepaids" :dues="dues" :total="total" :city="HARDCODED_CITY"
            :singlePaymentCost="singlePaymentCost" :selectedInstallments="selectedInstallments"
            :prereg-student-data="preregStudentData" :installment-details="installmentDetails"
            :loading-installments="loadingInstallments" :installment-error="installmentError" @showForm="showForm">
          </EmailSummary>
        </v-col>
      </v-row>
      <InstallmentSimulator :dialogVisible.sync="installmentsSimulator" :singlePaymentCost="singlePaymentCost"
        :total="total.cost" :saving="total.saving" :selectedInstallments="selectedInstallments"
        :installmentDetails="installmentDetails" :loadingInstallments="loadingInstallments"
        :installmentError="installmentError"></InstallmentSimulator>

    </v-container>
  </v-container>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import LeadInfo from '../Components/LeadInfo';
import PriceSimulator from '../Components/PriceSimulator';
import PreregistrationInfo from '../Components/PreregistrationInfo';
import Summary from '../Components/Summary';
import EmailSummary from '../Components/EmailSummary';
import InstallmentSimulator from '../Components/InstallmentSimulator.vue';
import {
  $prepaids, $preRegister, $billingProps, $discounts, $benefits, $scholarships,
  $terms, $studyPlans, $pagoAutogestionado, $careers, $studentToken, $paymentTerm
} from '../Services';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
  name: 'PreregistrationCreate',
  components: {
    Breadcrumbs,
    Button,
    LeadInfo,
    PriceSimulator,
    PreregistrationInfo,
    Summary,
    EmailSummary,
    InstallmentSimulator
  },
  data() {
    return {
      HARDCODED_CITY: { idCity: 1, idRegion: null, cityState: 'DEFAULT, TODOS, GLOBAL' },
      installmentsSimulator: false,
      careerId: null,
      isCreated: false,
      preregStudentData: null,
      validStudentData: false,
      loading: false,
      saleTerms: [],
      hubspotId: null,
      studentType: 7,
      referredFriendId: 10,
      careers: [],
      selectedCareer: null,
      selectedInstallments: 1,
      studyPlan: null,
      selectedZone: null,
      telephone: null,
      email: null,
      name: null,
      average: false,
      prices: [],
      selectedPrices: [],
      selectedModality: null,
      discounts: [],
      benefits: [],
      benefitToken: null,
      studentToken: null,
      selectedBenefit: null,
      referredFriend: null,
      scholarships: [],
      selectedScholarship: null,
      prepaids: [],
      dues: null,
      arancelAmount: null,
      selectedDiscounts: [],
      createButton: {
        loading: false,
        success: false,
        error: false,
        disabled: true
      },
      showEmailSummary: false,
      paymentLinkButton: {
        loading: false,
        success: false,
        error: false
      },
      withoutPayment: false,
      withoutDocumentation: false,
      globalDiscount: null,
      installmentsAmount: 0,
      singlePaymentCost: 0,
      installmentDetails: [],
      installmentError: null,
      loadingInstallments: false,
    };
  },
  computed: {
    ceoId() {
      if (this.$isTeclab) return 369
      else if (this.$isIPP) return 546
      else return null
    },
    canCreate() {
      return this.$permissions.portaladministrativo.admission.pre_registration.CREATE;
    },
    ...mapGetters({
      userId: 'commons/getUserID',
      userData: 'commons/getUserData'
    }),
    isBianualArancel() {
      return this.total.prices.filter(price => price.id == this.$sisConstants('modules.products.arancel')).length > 1
    },
    total() {
      const total = {
        prices: [],
        savings: [],
        cost: 0,
        saving: 0,
        netFees: 0
      }
      if (this.selectedPrices.length) {
        this.selectedPrices.forEach(price => {
          total.prices.push({
            key: price.key,
            id: price.id,
            name: price.label,
            amount: price.amount,
            isBilledIndividually: price.isBilledIndividually
          })
          total.savings.push({
            key: `Saving ${price.key}`,
            id: price.id,
            name: price.label,
            percentage: 0,
            amount: 0,
            discountId: null,
            benefitId: null,
            scholarshipId: null,
            details: []
          })
          total.cost += parseFloat(price.amount)
          if (!price.isBilledIndividually) total.netFees += parseFloat(price.amount)
        })
      }

      if (this.discounts.length) {
        const activeDiscounts = (this.selectedDiscounts && this.selectedDiscounts.length) ? this.selectedDiscounts : []
        activeDiscounts.forEach(discount => {
          
          if (discount.id == 9999) return
          const discountId = discount.id


          const matchingPrice = total.prices.find(price => price.id == discount.billingProduct.id)
          const discountProduct = total.savings.find(saving => saving.id == discount.billingProduct.id)

          if (discountProduct && matchingPrice) {
            const productPrice = total.prices.reduce((acc, price) => {
              if (price.id == discount.billingProduct.id) {
                acc += Number(price.amount) || 0;
              }
              return acc
            }, 0)

            let finalAmount, finalPercentage;
            if (discountProduct.id == this.$sisConstants('modules.products.matricula') || total.prices.filter(price => price.id == this.$sisConstants('modules.products.arancel')).length > 1) {
              finalAmount = discount.bianualAmount !== null && discount.bianualAmount !== undefined ? discount.bianualAmount : 0;
              finalPercentage = discount.bianualPercentage !== null && discount.bianualPercentage !== undefined ? discount.bianualPercentage : 0;
            } else {
              finalAmount = discount.bimesterAmount !== null && discount.bimesterAmount !== undefined ? discount.bimesterAmount : 0;
              finalPercentage = discount.bimesterPercentage !== null && discount.bimesterPercentage !== undefined ? discount.bimesterPercentage : 0;
            }

            const rawAmount = finalAmount > 0 ? finalAmount : productPrice * finalPercentage / 100;
            const weightedAmount = finalAmount > 0 ? finalAmount : ((productPrice - discountProduct.amount) * finalPercentage) / 100;

            const savingAmount = this.$isTeclab ? rawAmount : weightedAmount;

            discountProduct.amount += Number(savingAmount) || 0;
            discountProduct.percentage += finalPercentage;
            discountProduct.discountId = discountId;
            discountProduct.details.push({
              name: `${this.$t('modules.admission.preregistration.create.summary.discount')} - ${discountProduct.name}`,
              type: 'DISCOUNT',
              amount: Number(savingAmount) || 0,
              percentage: finalPercentage
            });

            if (!matchingPrice.isBilledIndividually) {
              total.netFees -= discountProduct.amount;
            }
            total.saving -= Number(savingAmount) || 0;
          }


        })
        const globalDiscount = this.discounts.find((discount) => discount.id == 9999);
        if (globalDiscount) {
          const rawAmount = total.netFees * globalDiscount.details[0].percentage / 100
          total.savings.push({
            key: `Saving ${globalDiscount.description}`,
            id: globalDiscount.id,
            name: globalDiscount.description,
            percentage: globalDiscount.details[0].percentage,
            amount: rawAmount,
            discountId: globalDiscount.id,
            benefitId: null,
            scholarshipId: null,
            details: [{
              name: `${this.$t('modules.admission.preregistration.create.summary.discount')} - ${globalDiscount.description}`,
              type: 'DISCOUNT',
              amount: rawAmount,
              percentage: globalDiscount.percentage
            }]
          })
          total.saving -= parseFloat(rawAmount)
        }
      }
      if (this.selectedBenefit) {
        if (this.selectedBenefit.details) {
          const [{ percentage: benefitPercentage, amount: benefitAmount, billingProduct }] = this.selectedBenefit.details
          const [benefitProduct] = total.savings.filter(saving => saving.id == billingProduct.id)
          if (benefitProduct) {
            const productPrice = total.prices.reduce((acc, price) => {
              if (price.id == billingProduct.id) {
                acc += price.amount
              }
              return acc
            }, 0)
            const rawAmount = benefitAmount || benefitPercentage >= 100 ? ((productPrice - benefitProduct.amount) * benefitPercentage) / 100 : productPrice * benefitPercentage / 100
            const weightedAmount = benefitAmount || ((productPrice - benefitProduct.amount) * benefitPercentage) / 100
            benefitProduct.percentage += parseFloat(benefitPercentage || 0)
            benefitProduct.amount += parseFloat(this.$isTeclab ? rawAmount : weightedAmount)
            benefitProduct.benefitId = this.selectedBenefit.id
            benefitProduct.details.push({
              name: `${this.selectedBenefit.description} - ${benefitProduct.name}`,
              type: 'BENEFIT',
              percentage: benefitPercentage,
              amount: this.$isTeclab ? rawAmount : weightedAmount
            })
            total.saving -= parseFloat(this.$isTeclab ? rawAmount : weightedAmount)
          }
        }
      }
      if (this.referredFriend) {
        if (total.savings.length != total.prices.length) total.savings.pop()
        if (total.prices.length) {
          total.savings.push({
            key: `${this.referredFriend.code}${this.referredFriend.id}`,
            name: this.referredFriend.type.meaning,
            percentage: null,
            amount: this.referredFriend.benefitReferredAmount,
            benefitId: this.referredFriend.id,
            referredFriend: true
          })
          total.saving -= parseFloat(this.referredFriend.benefitReferredAmount)
        }
      }
      if (this.selectedScholarship) {
        const scholarshipId = this.selectedScholarship.id
        this.selectedScholarship.scholarshipProduct.forEach(scholarship => {
          const [scholarshipProduct] = total.savings.filter(saving => saving.id == scholarship.billingProduct.id)
          if (scholarshipProduct) {
            const productPrice = total.prices.reduce((acc, price) => {
              if (price.id == scholarship.billingProduct.id) {
                acc += price.amount
              }
              return acc
            }, 0)
            //TODO: add raw and weighted amount
            scholarship.amount = scholarship.originalAmount || (productPrice * scholarship.percentage) / 100
            scholarship.percentage = scholarship.originalPercentage || ((scholarship.amount * 100) / productPrice).toFixed(2)
            scholarshipProduct.amount += parseFloat(scholarship.amount)
            scholarshipProduct.percentage += parseFloat(scholarship.percentage)
            scholarshipProduct.scholarshipId = scholarshipId
            scholarshipProduct.details.push({
              name: `${this.selectedScholarship.description} - ${scholarshipProduct.name}`,
              type: 'SCHOLARSHIP',
              percentage: scholarship.percentage,
              amount: scholarship.amount
            })
            total.saving -= parseFloat(scholarship.amount)
          }
        })
      }
      total.savings.map(saving => {
        const [price] = total.prices.filter(price => price.id == saving.id)
        if (saving.percentage > 100) {
          const priceAmount = (saving.id == this.$sisConstants('modules.products.arancel') && total.prices.filter(price => price.id == this.$sisConstants('modules.products.arancel')).length > 1) ? price.amount * 2 : price.amount
          saving.percentage = 100
          total.saving += saving.amount - priceAmount
          saving.amount = priceAmount
        }
        return saving
      })
      return total
    },
    shouldFetchInstallments() {
      return this.total.prices.length > 0 && this.selectedInstallments;
    }
  },
  mounted() {
    this.getSaleTerms()
    this.getCareers()
  },
  watch: {
    selectedPrices(newValues) {
      this.createButton.disabled = !newValues.length
      if (!newValues.length) {
        this.selectedModality = null
      }
    },
    shouldFetchInstallments: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.getInstallmentDetails();
        } else {
          this.installmentDetails = [];
          this.installmentError = null;
        }
      }
    },
    'total.prices': {
      handler() {
        if (this.shouldFetchInstallments) {
          this.getInstallmentDetails();
        }
      },
      deep: true
    }
  },
  methods: {
    applyDiscount(changedDiscount) {
      this.globalDiscount = changedDiscount;
      if (changedDiscount != null) {
        const descuento = {
          id: 9999,
          description: 'Ayuda complementaria',
          code: '9999',
          status: true,
          details: [
            {
              id: 9999,
              bimesterAmount: null,
              bianualAmount: null,
              bimesterPercentage: null,
              bianualPercentage: null,
              amount: null,
              percentage: changedDiscount,
            },
          ],
        };
        if (!this.discounts) {
          this.discounts = []
        }
        this.discounts.push(descuento);
      } else {
        this.discounts.every((discount, index) => {
          if (discount.id == 9999) {
            this.discounts.splice(index, 1);
            return false;
          }
          return true;
        });
      }
    },
    scrollToSection(refSection) {
      const container = this.$refs.preregistrationScroll
      container.scrollTop = this.$refs[refSection].$el.offsetTop - container.offsetTop
    },
    async getSaleTerms() {
      try {
        const { data } = await $terms.find('current-sales')
        this.saleTerms = data.content.sort((a, b) => (a.classStartDate > b.classStartDate) ? 1 : -1)
      } catch (err) {
        this.saleTerms = []
        throw err
      }
    },
    async getCareers() {
      try {
        const { data } = await $careers.find(null, null, { params: { page: 0, length: 500, status: true } })
        this.careers = data.content
      } catch (err) {
        this.careers = []
        throw err
      }
    },
    async getPrepaids() {
      try {
        // with cityId
        /*const {data: prepaids} = await $prepaids.find(null, null, { params: { termId: this.preregStudentData.selectedSaleTerm.id, careerId: this.selectedCareer.id, cityId: this.city.idCity || this.city.idRegion, studentType: this.studentType, average: this.average }})*/
        // with ceoId
        const { data } = await $prepaids.find(null, null, { params: { termId: this.preregStudentData.selectedSaleTerm.id, careerId: this.selectedCareer.id, ceoId: 546, studentType: this.studentType, average: this.average } })
        this.prepaids = data.sort((a, b) => (a.dayFrom > b.dayFrom) ? 1 : -1)
      } catch (err) {
        this.prepaids = []
        throw err
      }
    },
    async fetchPrices() {
      if (this.selectedCareer && this.selectedZone && !this.loading) {
        // Ensure zone has the correct structure
        const { id, zoneId: altZoneId } = this.selectedZone;
        const zoneId = id || altZoneId;
        
        if (!zoneId) {

          this.prices = [];
          return;
        }
        
        this.loading = true
        try {
          const { data: prices } = await $billingProps.findPriceByCarrerAndCity(`/products-with-price-by-zone?careerId=${this.selectedCareer.id}&zoneId=${zoneId}&studentType=${this.studentType}`)
          this.prices = prices.map((price) => {
            return {
              id: price.id,
              key: `${price.name}${price.id}`,
              label: price.name,
              amount: price.amount,
              selected: false,
              term: price.term,
              isBilledIndividually: price.isBilledIndividually,
              modality: price.modalityType
            }
          })
          this.preregStudentData.selectedSaleTerm = this.selectedPrices[0]?.term

          if (this.$isIPP) {
            this.getPrepaids()
          }

          try {
            const { data: discounts } = await $discounts.find('date', null, {
              params: {
                zoneId: this.selectedZone.id,
                idCareer: this.selectedCareer.id,
                studentType: this.studentType
              }
            });

            // Normalizar los discounts para que tengan la modalidad en el mismo formato que el precio 
            const normalizedDiscounts = discounts.map(discount => {
              return {
                ...discount,
                modality: discount.modality ? { id: discount.modality.value, meaning: discount.modality.meaning } : null
              }
            })
            
            // Filtrar los discounts para que solo se guarden los que tengan un price con mismo billingProduct.id y modalidad
            this.discounts = normalizedDiscounts.filter(discount => {
              const hasMatchingPrice = prices.some(price => {
                const sameProduct = price.id === discount.billingProduct.id
                if (!sameProduct) return false
                const priceModalityId = price.modalityType?.id || price.modality?.id
                const discountModalityId = discount.modality?.id || null
                return discountModalityId ? (priceModalityId === discountModalityId) : true
              })

              if (hasMatchingPrice) {
                discount.to = moment().locale('es').format('D [de] MMMM [del] YYYY')
                return true
              } else {
                return false
              }
              
            })
            
          } catch {
            this.discounts = []
          }

          try {
            const { data: benefits } = await $benefits.find('date')
            if (this.benefitToken) {
              try {
                const { data: dataToken } = await $studentToken.find(null, null, { params: { token: this.benefitToken } })
                this.studentToken = dataToken
              } catch {
                this.studentToken = null
              }
            } else {
              this.studentToken = null
            }
            this.benefits = benefits.filter(benefit => {
              if (benefit.details) {
                benefit.details = benefit.details.reduce((acc, product) => {
                  const benefitPrice = prices.filter(price => price.id == product.billingProduct.id)
                  if (benefitPrice.length) acc.push(product)
                  return acc
                }, [])
                return !!benefit.details.length
              } else {
                if (this.studentToken) {
                  if (benefits.find(benefit => benefit.id == this.studentToken.id && benefit.type.value == this.referredFriendId)) {
                    return true
                  }
                }
                return false
              }
            })
          } catch {
            this.benefits = []
            this.benefitToken = null
          }
          try {
            const { data: scholarships } = await $scholarships.find(null, null, { params: { page: 0, length: 500 } })
            this.scholarships = scholarships.content.filter(scholarship => {
              scholarship.scholarshipProduct = scholarship.scholarshipProduct.reduce((acc, product) => {
                const priceAmount = prices.reduce((acc, price) => {
                  if (price.id == product.billingProduct.id) {
                    acc += price.amount
                  }
                  return acc
                }, 0)
                if (priceAmount) {
                  product.originalAmount = product.amount
                  product.originalPercentage = product.percentage
                  acc.push(product)
                }
                return acc
              }, [])
              return !!scholarship.scholarshipProduct.length
            })
          } catch {
            this.scholarships = []
          }
        } catch (error) {
          this.prices = []
          this.$store.dispatch('commons/openPopUp', {
            title: this.$t('alert'),
            content: [{ value: this.$t('modules.admission.preregistration.create.errorMessage.withoutPrice') }],
            actionPrimary: { text: this.$t('actions.accept'), callback() { } },
            icon: { color: 'warning', name: 'mdi-alert' },
            color: 'primary',
          });
          throw error
        } finally {
          this.loading = false
        }
      }
    },
    enablePriceSimulator(career, zone, hubspotId) {
      this.hubspotId = hubspotId
      this.selectedCareer = career
      this.selectedZone = zone
      this.prices = []
      this.selectedPrices = []
      if (career && zone && hubspotId) this.fetchPrices()
    },
    averageChange(average) {
      this.average = average
      this.getPrepaids()
    },
    showForm() {
      this.showEmailSummary = false
      this.$el.scrollIntoView();
    },
    async sendPaymentLink() {
      try {
        this.paymentLinkButton.loading = true
        const { name, lastname } = this.userData;
        const body = {
          email: this.preregStudentData.email,
          name: this.preregStudentData.name,
          identification: this.preregStudentData.identification,
          adviser: this.$options.filters.$capitalize(`${lastname}, ${name}`)
        }
        await $pagoAutogestionado.create(body)
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.admission.preregistration.create.successMessage.paymentLink'),
          actionPrimary: {
            text: this.$t('actions.close'), callback() { }
          },
          icon: { color: 'success', name: 'mdi-check' },
          color: 'primary',
        });
        this.paymentLinkButton.success = true
      } catch (err) {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.admission.preregistration.create.errorMessage.paymentLink'),
          actionPrimary: {
            text: this.$t('actions.close'), callback() { }
          },
          icon: { color: 'error', name: 'mdi-close' },
          color: 'primary',
        });
        this.paymentLinkButton.error = true
        throw err
      } finally {
        this.paymentLinkButton.loading = false
        this.paymentLinkButton.error = false
        this.paymentLinkButton.success = false
      }
    },
    validatePreregistration() {
      this.createButton.loading = true
      this.$refs.preregistrationInfo.validateStudentData()
    },
    async createPreregistration() {
      this.validStudentData = true
      try {
        if (this.isBianualArancel) {
          const firstArancel = this.total.savings.find(saving => saving.id == this.$sisConstants('modules.products.arancel') && saving.amount)
          const secondArancel = this.total.savings.find(saving => saving.id == this.$sisConstants('modules.products.arancel') && !saving.amount)
          if (firstArancel) {
            secondArancel.discountId = firstArancel.discountId
            secondArancel.benefitId = firstArancel.benefitId
            secondArancel.scholarshipId = firstArancel.scholarshipId
            secondArancel.details = firstArancel.details
            this.arancelAmount = firstArancel.amount
          }
        }
        const { preregStudentData } = this
        if (this.$isTeclab) {
          try {
            const { data } = await $studyPlans.find(null, null, { params: { careerId: preregStudentData.selectedCareer.id, filterByStatus: true } })
            preregStudentData.selectedStudyPlan = {
              value: data.content.pop().id
            }
          } catch {
            preregStudentData.selectedStudyPlan = null
          }
        }
        const newPreregistration = {
          hubspotId: this.hubspotId === true ? null : this.hubspotId,
          name: preregStudentData.name,
          lastname: preregStudentData.lastname,
          birthDate: preregStudentData.birthDate,
          birthPlace: this.$sisConstants('defaultCity'),
          nationality: preregStudentData.selectedNationality,
          maritalStatus: preregStudentData.selectedMaritalStatus,
          genderId: preregStudentData.selectedGender,
          identificationType: preregStudentData.selectedIdentificationType,
          identification: preregStudentData.identification,
          serialNumber: preregStudentData.serialNumber,
          email: preregStudentData.email,
          cellphone: preregStudentData.telephone,
          phone: null,
          address: {
            street: `${preregStudentData.addressCountry}, ${preregStudentData.addressCity}, ${preregStudentData.address}`,
            number: preregStudentData.number,
            floor: preregStudentData.floor,
            apartment: preregStudentData.apartment,
            zipCode: preregStudentData.zipCode,
            cityWithState: this.HARDCODED_CITY,
            cityId: this.HARDCODED_CITY.idCity,
          },
          institution: preregStudentData.selectedInstitution,
          levelType: preregStudentData.selectedStudentLevel,
          userId: this.userId,
          termId: this.selectedPrices[0]?.term.id,
          cityId: this.HARDCODED_CITY.idCity,
          ceoId: this.ceoId,
          careerId: preregStudentData.selectedCareer.id,
          studyPlanId: preregStudentData.selectedStudyPlan?.value || null,
          good_average: this.average,
          benefitToken: this.benefitToken,
          withoutPayment: this.withoutPayment,
          withoutDocumentation: this.withoutDocumentation,
          globalDiscount: this.globalDiscount,
          modality: this.selectedModality,
          preregistation: this.total.savings.length ? this.total.savings.map(saving => {
            if (saving?.referredFriend) {
              return {
                installment: null,
                installmentAmount: null,
                billingProduct: null,
                amount: null,
                discount: null,
                benefit: {
                  id: saving.benefitId,
                  referred: true,
                  community: null,
                  credits: saving.amount
                },
                scholarship: null,
              }
            } else if (saving.id != 9999) {
              const [price] = this.total.prices.filter(price => price.id == saving.id)
              const savingAmount = saving.id == this.$sisConstants('modules.products.arancel') && this.isBianualArancel ? (this.arancelAmount / 2) : saving.amount
              const amount = price.amount - savingAmount
              return {
                installments: this.selectedInstallments ? this.selectedInstallments : 1,
                installment: this.dues ? (this.dues?.value || this.dues.due.installments) : 1,
                installmentAmount: this.dues ? parseFloat((amount / (this.dues?.value || this.dues.due.installments)).toFixed(2)) : parseFloat(amount.toFixed(2)),
                billingProduct:
                {
                  id: saving.id
                },
                amount: price.amount,
                discount: saving.discountId ? { id: saving.discountId } : null,
                benefit: saving.benefitId ? {
                  id: saving.benefitId,
                  referred: false,
                  community: { id: this.selectedBenefit?.community } || null,
                  credits: saving.details.find(detail => detail.type == 'BENEFIT')?.amount / (saving.id == this.$sisConstants('modules.products.arancel') && this.isBianualArancel ? 2 : 1) || 0
                } : null,
                scholarship: saving.scholarshipId ? { id: saving.scholarshipId } : null,
              }
            } else {
              return null;
            }
          }).filter((item) => item !== null) : null
        }
        await $preRegister.create(newPreregistration);
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.admission.preregistration.create.successMessage.preregistration'),
          actionPrimary: {
            text: this.$t('actions.close'), callback() { }
          },
          icon: { color: 'success', name: 'mdi-check' },
          color: 'primary',
        });
        this.createButton.success = true;
        this.createButton.loading = false;
        this.isCreated = true;
      } catch (error) {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.admission.preregistration.create.errorMessage.preregistration'),
          actionPrimary: {
            text: this.$t('actions.close'), callback() { }
          },
          icon: { color: 'error', name: 'mdi-close' },
          color: 'primary',
        });
        this.createButton.loading = false;
        this.createButton.error = true;
        throw error;
      }
    },
    createPreregistrationError() {
      this.createButton.error = true;
      this.createButton.loading = false;
    },
    clearData() {
      this.$router.go(0)
    },
    resetButtonValues() {
      this.createButton.loading = false;
      this.createButton.success = false;
      this.createButton.error = false;
    },
    calculatePaymentCosts() {
      const singlePaymentCost = this.total.prices.reduce((acc, price) => {
        if (price.isBilledIndividually) {
          acc += price.amount;
        }
        return acc;
      }, 0);

      const installmentsAmount = this.total.prices.reduce((acc, price) => {
        if (!price.isBilledIndividually) {
          acc += price.amount;
        }
        return acc;
      }, 0);

      const installmentSavings = this.total.savings.reduce((acc, saving) => {
        const isBilledIndividually = this.total.prices.find(price => price.id === saving.id)?.isBilledIndividually;
        if (!isBilledIndividually) {
          acc += saving.amount;
        }
        return acc;
      }, 0);

      const singlePaymentSavings = this.total.savings.reduce((acc, saving) => {
        const isBilledIndividually = this.total.prices.find(price => price.id === saving.id)?.isBilledIndividually;
        if (isBilledIndividually) {
          acc += saving.amount;
        }
        return acc;
      }, 0);

      this.singlePaymentCost = singlePaymentCost - singlePaymentSavings;
      this.installmentsAmount = installmentsAmount - installmentSavings;
    },
    async getInstallmentDetails() {
      if (!this.shouldFetchInstallments) return;
      this.loadingInstallments = true;
      this.installmentError = null;
      try {
        this.calculatePaymentCosts();
        const data = {
          total: this.total.saving != null ? this.total.cost + this.total.saving : this.total.cost,
          installments: this.selectedInstallments,
          tuition: this.singlePaymentCost,
          classStartDate: new Date().toISOString()
        };
        const response = await $paymentTerm.create(data);
        this.installmentDetails = response.data.installments;
      } catch (error) {
        this.installmentDetails = [];
        this.installmentError = this.$t('modules.admission.preregistration.create.installmentSimulator.errorMessage');
        this.$store.dispatch('commons/openPopUp', {
          key: Date.now(),
          title: this.$t('alert'),
          content: [{ value: this.installmentError }],
          actionPrimary: {
            text: this.$t('actions.accept'),
            callback() { },
          },
          icon: { color: 'warning', name: 'mdi-alert' },
          color: 'primary',
        });
        throw error;
      } finally {
        this.loadingInstallments = false;
      }
    },
    async openInstallmentsModal() {
      if (!this.selectedInstallments || !this.selectedPrices) return
      this.calculatePaymentCosts();
      try {
        await this.getInstallmentDetails();
        if (this.installmentDetails.length > 0) {
          this.installmentsSimulator = true;
        }
      } catch (error) {
        this.installmentsSimulator = false;
      }
    },
  }
};
</script>

<style lang="sass" scoped>

.stepper-header-height
  height: auto

.preregistration-scroll
  overflow-y: auto
  overflow: -moz-scrollbars-none
  -ms-overflow-style: none
  height: 100vh
  scroll-behavior: smooth

.preregistration-scroll::-webkit-scrollbar
  width: 0 !important


</style>
